import { useMemo, useState } from 'react'
import type { Clinic, Provider, EligibilityError } from '../types'
import type { PayerOption } from './PayerSearch'
import PayerSearch from './PayerSearch'
import type { SelectedPayer } from './PayerSearch'
import { canSubmitManualCheck, isManualMemberIdOptional } from '../utils/manualCheckGate'
import { scrubVendorText } from '../utils/scrubVendorText'
import { logFeature } from '../services/activity'
import DateInput from './DateInput'

export interface ManualFormData {
  firstName: string
  lastName: string
  dateOfBirth: string
  memberId: string
  payerName: string
  tradingPartnerServiceId: string
  /** Stedi's stable internal ID for the selected payer. Used to look up
   *  payer-specific rules (e.g. memberId-optional registry). Null for
   *  RPA-routed payers, which don't exist in the Stedi directory. */
  stediPayerId: string | null
  gender: 'M' | 'F' | ''
  /** Appointment date (YYYY-MM-DD) — required for VSP RPA checks. */
  appointmentDate: string
  /** Flow (linear correction chain) the resulting check joins. Set by prefill
   *  when the form was opened to correct an existing check or re-check a
   *  cockpit row; rides through the form untouched. Absent → new flow. */
  flowId?: string
  /** The check being corrected/re-checked — set by the same prefills, rides
   *  through untouched. The server inherits patient contact + SMS-recovery
   *  candidacy from it; absent → a fresh manual lookup, which stores no
   *  contact and never enters the SMS recovery loop (the form deliberately
   *  collects no phone/email — it's a lookup tool, not an outreach surface). */
  sourceCheckId?: string
  /** The cockpit appointment row this form was opened from, when that row
   *  carries no check yet. Rides through untouched so the server can stamp the
   *  resulting check onto it — otherwise the row keeps reading "Cannot check"
   *  after staff have already answered it. Absent for every other entry. */
  appointmentId?: string
}

const emptyForm: ManualFormData = {
  firstName: '', lastName: '', dateOfBirth: '', memberId: '',
  payerName: '', tradingPartnerServiceId: '', stediPayerId: null, gender: '',
  appointmentDate: '',
}

interface EligibilityFormProps {
  providers: Provider[]
  selectedProviderId: string
  onProviderChange: (id: string) => void
  checking: boolean
  error: EligibilityError | null
  onRetry?: () => void
  onCheck: (manualForm: ManualFormData) => void
  onUploadCsv?: () => void
  /** Override cloud function payer search (demo mode). */
  searchFn?: (input?: { query?: string; clinicId?: string }) => Promise<{ payers: PayerOption[] }>
  /** Pre-fill form fields (e.g. from a cockpit appointment row).
   *  Parent must bump `key` to re-mount when this changes. */
  initialData?: Partial<ManualFormData>
  /** Clinic context — drives the payer dropdown's most-used-first ordering. */
  clinicId?: string
  /** Used to label provider options with their clinic when admins see providers across clinics. */
  clinics?: Clinic[]
  /** Drives the zero-providers empty state: managers/admins get a link to
   *  the Doctors page; plain users are told to ask their practice admin. */
  isManagerOrAdmin: boolean
  /** Navigate to the Doctors page (the empty state's assign path). */
  onGoToDoctors: () => void
  /** Re-fetch the provider directory — the empty state's refresh affordance
   *  for when a doctor gets assigned from elsewhere (no live subscription). */
  onRefreshProviders: () => Promise<void>
}

export default function EligibilityForm({
  providers, selectedProviderId, onProviderChange,
  checking, error, onRetry, onCheck, onUploadCsv, searchFn, initialData,
  clinicId, clinics, isManagerOrAdmin, onGoToDoctors, onRefreshProviders,
}: EligibilityFormProps) {
  const clinicNameById = useMemo(
    () => new Map((clinics ?? []).map(c => [c.id, c.name])),
    [clinics],
  )
  const showProviderClinic = (clinics?.length ?? 0) > 1
  const [manualForm, setManualForm] = useState<ManualFormData>(() => initialData ? { ...emptyForm, ...initialData } : emptyForm)

  function handlePayerSelect(payer: SelectedPayer) {
    setManualForm(f => ({
      ...f,
      payerName: payer.name,
      tradingPartnerServiceId: payer.tradingPartnerServiceId,
      stediPayerId: payer.stediId,
    }))
  }

  function handlePayerClear() {
    setManualForm(f => ({
      ...f,
      payerName: '',
      tradingPartnerServiceId: '',
      stediPayerId: null,
    }))
  }

  // Submission rules live in utils/manualCheckGate.ts (pure, unit-tested).
  // A provider is required for every payer — vision included, since the
  // verifyEligibility callable rejects a providerless request outright.
  const memberIdOptional = isManualMemberIdOptional(manualForm)
  const canCheck = canSubmitManualCheck(manualForm, selectedProviderId)

  // Zero providers assigned to this clinic — a config state, not a form
  // state: nothing the user types can fix it, so the Provider section
  // explains it instead of rendering an empty dropdown (the Lakewood
  // dead-button report).
  const noProvidersAssigned = providers.length === 0
  const [refreshingProviders, setRefreshingProviders] = useState(false)

  // Attempted-submit validation, same shape as ClinicsList / DoctorsList /
  // IntegrationsList: the button stays clickable, the
  // click marks the form attempted, and the empty required fields mark
  // themselves. The button is disabled ONLY for the zero-provider case —
  // that one is a config state no amount of typing fixes, and the panel
  // above already explains it.
  const [attempted, setAttempted] = useState(false)
  /** Red border on a required field the user left empty, once they've tried. */
  function inv(missing: boolean): string {
    return attempted && missing ? ' is-invalid' : ''
  }

  async function handleRefreshProviders() {
    setRefreshingProviders(true)
    try {
      await onRefreshProviders()
    } finally {
      setRefreshingProviders(false)
    }
  }

  function handleSubmit() {
    setAttempted(true)
    // Bail before the telemetry call — a click on an incomplete form is a
    // validation event, not an eligibility check.
    if (!canCheck) return
    logFeature('eligibility')
    onCheck(manualForm)
  }

  return (
    <main className="dashboard-main">
      <div className="eligibility-form-card">
        <h2 className="eligibility-form-card__title">Eligibility Verification</h2>
        <p className="eligibility-form-card__subtitle">
          Check patient insurance eligibility and coverage details
        </p>

        <div className="form-sections">
          <section className="form-section">
            <h3 className="form-section__title">Patient</h3>
            <div className="form-fields">
              <div className="form-field">
                <label className="form-field__label" htmlFor="manual-first">First Name</label>
                <input
                  id="manual-first"
                  className={`form-field__input${inv(!manualForm.firstName)}`}
                  value={manualForm.firstName}
                  onChange={e => setManualForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="manual-last">Last Name</label>
                <input
                  id="manual-last"
                  className={`form-field__input${inv(!manualForm.lastName)}`}
                  value={manualForm.lastName}
                  onChange={e => setManualForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Doe"
                />
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="manual-dob">Date of Birth</label>
                <DateInput
                  id="manual-dob"
                  className="form-field__input"
                  value={manualForm.dateOfBirth}
                  onValueChange={val => setManualForm(f => ({ ...f, dateOfBirth: val }))}
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section__title">Insurance</h3>
            <div className="form-fields">
              <PayerSearch
                value={manualForm.payerName}
                onSelect={handlePayerSelect}
                onClear={handlePayerClear}
                searchFn={searchFn}
                autoResolveOnMount
                clinicId={clinicId}
                // Typed text that never resolved to a directory pick looks
                // filled in but isn't — the one required field where a bare
                // red border wouldn't explain itself.
                invalidMessage={attempted && !manualForm.tradingPartnerServiceId
                  ? 'Select the patient’s insurance from the list.'
                  : null}
              />
              <div className="form-field">
                <label className="form-field__label" htmlFor="manual-member">
                  Member ID{memberIdOptional && <span className="form-field__optional"> (optional)</span>}
                </label>
                <input
                  id="manual-member"
                  className={`form-field__input${inv(!memberIdOptional && !manualForm.memberId)}`}
                  value={manualForm.memberId}
                  onChange={e => setManualForm(f => ({ ...f, memberId: e.target.value }))}
                  placeholder="ABC123456"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section__title">Provider</h3>
            <div className="form-fields">
              {noProvidersAssigned ? (
                <div className="provider-empty" role="status">
                  <p className="provider-empty__title">No doctors are assigned to this clinic yet</p>
                  <p className="provider-empty__text">
                    {isManagerOrAdmin ? (
                      <>
                        Eligibility checks need an assigned doctor. Assign one on the{' '}
                        <button type="button" className="provider-empty__link" onClick={onGoToDoctors}>
                          Doctors page
                        </button>.
                      </>
                    ) : (
                      'Eligibility checks need an assigned doctor. Ask your practice admin to assign one.'
                    )}
                  </p>
                  <button
                    type="button"
                    className="provider-empty__refresh"
                    onClick={handleRefreshProviders}
                    disabled={refreshingProviders}
                  >
                    {refreshingProviders ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
              ) : (
                <div className="form-field form-field--full">
                  <label className="form-field__label" htmlFor="provider-select">Provider</label>
                  <select
                    id="provider-select"
                    className="form-field__select"
                    value={selectedProviderId}
                    onChange={e => onProviderChange(e.target.value)}
                  >
                    {providers.map(p => {
                      const clinicName = showProviderClinic
                        ? p.clinicIds.map(id => clinicNameById.get(id)).filter(Boolean).join(', ') || undefined
                        : undefined
                      return (
                        <option key={p.id} value={p.id}>
                          {p.lastName}, {p.firstName}{clinicName ? ` [${clinicName}]` : ''}
                        </option>
                      )
                    })}
                  </select>
                </div>
              )}
            </div>
          </section>

        </div>

        <div className="form-actions">
          <button
            className="check-btn"
            disabled={noProvidersAssigned || checking}
            onClick={handleSubmit}
          >
            {checking ? 'Checking...' : 'Check Eligibility'}
          </button>
          {onUploadCsv && (
            <button className="csv-upload-toggle" onClick={onUploadCsv}>
              Upload CSV
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={`error-banner error-banner--${error.category}`}>
          <div className="error-banner__content">
            <p className="error-banner__title">{error.title}</p>
            <p className="error-banner__text">{scrubVendorText(error.message)}</p>
            <p className="error-banner__guidance">{error.guidance}</p>
          </div>
          {onRetry && (
            <button
              className="error-banner__retry"
              onClick={onRetry}
              disabled={checking}
            >
              Try Again
            </button>
          )}
        </div>
      )}
    </main>
  )
}
